/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { ReactNode, useCallback, useEffect, useMemo } from "react";
import { getLiveAnalysisTableColDefs } from "../../components/table/CustomHeader";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { AgGridReact } from "@ag-grid-community/react";
import { InfiniteRowModelModule } from "@ag-grid-community/infinite-row-model";
import { changeSinceDate, fetchLiveAnalysisData, setFilterAnomalies } from "../../store/liveAnalysisSlice";
import { PageContent } from "../../components/PageContent/PageContent";
import './LiveAnalysisPage.scss';
import '../../components/shared/Table/styles.scss';
import {
  GridReadyEvent,
  IDatasource,
  ModuleRegistry,
} from "@ag-grid-community/core";
import { useNavigate } from "react-router-dom";
import { Box } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import moment from "moment";
import { ModelConfigs } from "../AnalysisPage/types";
import { DesignItCheckbox } from "@design-it/react-library";


ModuleRegistry.registerModules([InfiniteRowModelModule]);

interface CardProps {
  children: ReactNode;
  className?: string;
}

export const Card: React.FC<CardProps> = ({ children, className = "" }) => (
  <div className={`rounded-2xl shadow-md ${className}`}>{children}</div>
);

export const CardContent: React.FC<CardProps> = ({ children, className = "" }) => (
  <div className={`p-4 ${className}`}>{children}</div>
);

interface StatCardProps {
  label: string;
  value: string | number;
  variant?: "default" | "efficiency";
}

export const StatCard: React.FC<StatCardProps> = ({ label, value, variant = "default", }) => {
  const variantStyles =
    variant === "efficiency"
      ? "border border-[var(--mat-sys-Error,#FFB4AB)] bg-[var(--surfaces-error-surface-tint-12,rgba(255,180,171,0.12))]"
      : "bg-[var(--mat-sys-Surface-Bright,#2F2F4B)]";
  return (
    <Card className={`flex flex-col items-start gap-4 p-4 rounded-[12px] flex-1 ${variantStyles} w-full`}>
      <CardContent className="flex flex-col items-start gap-2 p-0 w-full">
        <div
          className="flex justify-between items-center leading-[28px] text-white w-full"
          style={{
            fontFamily: "\"Frutiger LT 45 Light\"",
            letterSpacing: "0px",
            fontSize: "large",
          }}
        >
          <span>{label}</span>
          <span className="whitespace-nowrap">&nbsp;{value}</span>
        </div>
      </CardContent>
    </Card>
  );
};

export const LiveAnalyzerDashboard = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const modelConfigs = useAppSelector(state => state.user.modelConfigs);
  const currentUser = useAppSelector(state => state.user.currentUser); 
  const liveAnalysisCounts = useAppSelector(state => state.liveAnalysis.counts);
  const sinceDate = useAppSelector(state => state.liveAnalysis.sinceDate);
  const filterAnomalies = useAppSelector(state => state.liveAnalysis.filterAnomalies);
  
  const [gridApi, setGridApi] = React.useState<any>(null);

  const liveAnalysisColDefs = useMemo(() => {
    if(!modelConfigs) {
      return [];
    }
    return getLiveAnalysisTableColDefs(modelConfigs as ModelConfigs);
  }, [modelConfigs]);

  useEffect(() => {
    dispatch(fetchLiveAnalysisData({}));
  }, [dispatch, currentUser]);

  const getRows = async (params: {
    startRow: number;
    endRow: number;
    
    successCallback: (rowsThisPage: any[], lastRow?: number) => void;
    
    failCallback: (error: any) => void;
  }) => {

    const pageSize = 20;
    const pageNumber = Math.floor(params.startRow / pageSize) + 1;

    const result = await dispatch(fetchLiveAnalysisData({pageSize: pageSize, pageNumber : pageNumber}));
    if(result.meta.requestStatus !== "fulfilled") {
      params.failCallback(result.meta);
      return;
    }
    const newData = (result.payload as any)?.items || [];
    let lastRow = undefined;
    if (newData.length < pageSize) {
      lastRow = params.startRow + newData.length;
    }

    params.successCallback(newData, lastRow);
  };

  const [intervalId, setIntervalId] = React.useState<NodeJS.Timer | undefined>(undefined);
  const [isScrolled, setIsScrolled] = React.useState(true);

  useEffect(() => {
    return () => {
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    if(!gridApi) return;
    if(isScrolled && intervalId) {
      clearInterval(intervalId);
      setIntervalId(undefined);
    } else if(!isScrolled && !intervalId) {
      setIntervalId(setInterval(async () => {
        const result = await dispatch(fetchLiveAnalysisData({since: true}));
        const newData = (result.payload as any)?.items || [];
        if(newData.length > 0) {
          gridApi.purgeInfiniteCache();
        }
      }, 3000));
    }
  }, [dispatch, gridApi, intervalId, isScrolled]);

  
  const onGridReady = useCallback((event: GridReadyEvent<any, any>) => {
    const dataSource: IDatasource = {
      rowCount: undefined,
      getRows: getRows,
    };
    setGridApi(event.api);
    setIsScrolled(false);
    event.api.setGridOption("datasource", dataSource);
    const el = document.getElementById("scrollable-grid");
    el?.addEventListener("scroll", () => {
      if(el.scrollTop > 150) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    });
  }, []);

  
  const onRowClick = (row: any) => {
    const selectedRowId = row.data?.id;
    if (selectedRowId) {
      navigate(`/live-analysis/${selectedRowId}`);
    }
  };

  const liveAnalysisEfficiency = useMemo(() => {
    if (!liveAnalysisCounts) return 0;
    const anomalies = liveAnalysisCounts?.Anomalies || 0;
    const ok = liveAnalysisCounts?.OK || 0;
    return ok > 0 ? Math.round(anomalies / ok * 100) : 0;
  }, [liveAnalysisCounts]);

  return (
    <PageContent
      title={"Live Analyzer"}
      showButtonBar={false}
    > 
      <Box sx={{ position: 'absolute', top: 0, right: 0, padding: '0 32px' }}>
        <div className="flex items-center gap-2">
          <div className="mt-[7.5px]">
            <DesignItCheckbox 
              checked={filterAnomalies}
              onClick={async () => {
                const waitFn = async () => {
                  await dispatch(setFilterAnomalies(!filterAnomalies));
                  gridApi.setGridOption('datasource', {
                    rowCount: undefined,
                    getRows: getRows,
                  });
                };
                waitFn();
              }}
            >
              Show only anomalies
            </DesignItCheckbox>
          </div>
          <LocalizationProvider dateAdapter={AdapterMoment}>
            <DatePicker
              label="Start Date"
              sx={{border: 'none'}}
              value={moment(sinceDate * 1000)}
              onChange={async (e) => {
                await dispatch(changeSinceDate(e?.unix()));
                gridApi.setGridOption('datasource', {
                  rowCount: undefined,
                  getRows: getRows,
                });
              }} 
            />
          </LocalizationProvider>
        </div>
      </Box>

      <div className="grid grid-cols-4 gap-6 mb-6 my-5">
        <StatCard label="Bolting Operations" value={liveAnalysisCounts ? liveAnalysisCounts.Total : '-'} />
        <StatCard label="NOK bolting Operations" value={liveAnalysisCounts ? liveAnalysisCounts.NOK : '-'} />
        <StatCard label="OK bolting Operations" value={liveAnalysisCounts ? liveAnalysisCounts.OK : '-'} />
        <StatCard label="Efficiency Potential" value={liveAnalysisCounts ? `${liveAnalysisEfficiency}%` : '-'} variant="efficiency" />
      </div>
      <div className="ag-theme-material" id="scrollable-grid">
        <AgGridReact 
          loading={false}
          columnDefs={liveAnalysisColDefs}
          domLayout="autoHeight"
          rowClass="table-row"
          rowBuffer={3}
          rowModelType={"infinite"}
          cacheBlockSize={20}
          cacheOverflowSize={10}
          maxConcurrentDatasourceRequests={5}
          infiniteInitialRowCount={1000}
          maxBlocksInCache={200}
          headerHeight={48}
          rowHeight={48}
          onGridReady={onGridReady}
          onRowClicked={onRowClick}
          overlayLoadingTemplate={`<span class="ag-overlay-loading-center">Loading...</span>`}
          overlayNoRowsTemplate={
            liveAnalysisCounts?.Total === 0
              ? `<div class="custom-no-rows">No Rows To Show</div>`
              : ""
          }
        />
      </div>
    </PageContent>
  );
};
